import { navigate, PageProps } from 'gatsby';
import { ReactNode, useEffect } from 'react';
import { Layout } from 'components/layout/Layout';
import { PublicLayout } from 'components/layout/PublicLayout';
import { ResultsLayout } from 'components/layout/ResultsLayout';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { ServerError } from 'components/errorPages/ServerError';
import { AccessError } from 'components/errorPages/AccessError';
import { RoleAccessDenied } from 'components/errorPages/RoleAccessDenied';
import { NotFound } from 'components/errorPages/NotFound';
import { useTheme } from 'hooks/useTheme';
import { useIncidentNotification } from 'hooks/useIncidentNotification';
import { useUser } from 'services/User';
import { useUserData } from 'services/api/UserData';
import {
    canAccessPath,
    clearDeliberateHomeNav,
    getLicenseDefaultPath,
    isDeliberateHomeNav,
} from 'services/accessControl';
import { signIn } from 'services/authenticator';
import { canAccessStatsPage } from 'services/statsAccessGranting';

import '@opswat/ui/styles';
import '../../tailwind.css';
import 'components/WrapPage.css';

const PUBLIC_ROUTES = [
    '/',
    '/scan',
    '/extension/get-apikey',
    '/product',
    '/threat-intelligence',
    '/integrations-overview',
    '/blog',
];
const AUTH_OPTIONAL_ROUTES = ['/licensing', '/cve-vulnerabilities', '/integrations'];

const HIDE_AS_NOT_FOUND_PREFIXES = ['/store'];

type WrapPageProps = Omit<PageProps, 'children'> & { children?: ReactNode };

// The 404 page is served both at `/404` directly and as the fallback for any unknown
// route (where `location.pathname` is the attempted URL but `path` is the 404 page).
const isNotFoundPage = (pathname: string, path?: string): boolean =>
    pathname === '/404' || pathname === '/404.html' || path === '/404' || path === '/404/' || path === '/404.html';

export const WrapPage = ({ children, ...props }: WrapPageProps) => {
    const pathname = props.location.pathname;
    // Prefix match so nested public pages (e.g. /product/community, /product/enterprise) stay
    // public with their parent. Safe for the '/' entry: it only ever matches '/' exactly, since
    // no real pathname starts with '//'.
    const isPublic = PUBLIC_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
    // Prefix match so dynamic children (e.g. /cve-vulnerabilities/CVE-2024-3094) stay auth-optional too.
    const isAuthOptional = AUTH_OPTIONAL_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
    const { user } = useUser();
    const { error, isLoading, isSessionLoading } = useUserData({
        onSuccess: () => {
            if (pathname.startsWith('/extension/get-apikey') && window) {
                window.close();
            }
        },
    });
    useIncidentNotification();

    useTheme();

    useEffect(() => {
        document.querySelector('main')?.scrollTo({ top: 0 });
        window.scrollTo({ top: 0 });
    }, [pathname]);

    // Single landing decision (MDCL-18276): every authenticated arrival at the root — direct visit or
    // the post-login redirect — goes to its license default. Waits for `isLoading` so the license
    // type is present before choosing between dashboard and analyze. Deliberate in-app home
    // navigation (logo / home buttons, via navigateToHomePage) is exempt, so logged-in users can
    // still reach the homepage on purpose; the in-memory intent dies on full page loads, keeping
    // the post-login redirect intact.
    const isRoutedToLicenseDefault =
        !isLoading && pathname === '/' && user?.isLoggedIn === true && !isDeliberateHomeNav();

    // The home-nav exemption is one visit's worth of intent — drop it once the user leaves `/`.
    useEffect(() => {
        if (pathname !== '/') clearDeliberateHomeNav();
    }, [pathname]);

    // Explore Data (stats) is sign-in gated: guests go straight to login and come back to the same page
    // afterwards (signIn's return path). Logged-in users outside @opswat.com still get the 404 below.
    const isStatsPage = pathname === '/explore-data' || pathname.startsWith('/explore-data/');
    const isRoutedToLogin = !isLoading && isStatsPage && user?.isLoggedIn !== true;

    useEffect(() => {
        if (isRoutedToLogin) {
            signIn(pathname);
        }
    }, [isRoutedToLogin, pathname]);

    useEffect(() => {
        if (isRoutedToLicenseDefault) {
            void navigate(getLicenseDefaultPath(user), { replace: true });
        }
    }, [isRoutedToLicenseDefault, user]);

    // MDCI-1272: public routes other than `/` never wait. `/` and the auth-optional routes only need the
    // guest-or-signed-in answer from `/api/variables`; a signed-in user there still waits for the full load,
    // so the license-default redirect above fires without the guest page flashing. Every other route keeps
    // the full gate (session + apikey). The user is always still loading when Gatsby builds the HTML, so
    // skipping the gate on public routes is also what lets crawlers get the blog post instead of a spinner
    // (MDCI-1262); the server and first client render both take the same branch, so hydration matches.
    const isGuestOnSessionRoute = (pathname === '/' || isAuthOptional) && user?.isLoggedIn !== true;
    const isGateLoading = !(isPublic && pathname !== '/') && (isGuestOnSessionRoute ? isSessionLoading : isLoading);

    if (isGateLoading) {
        return <LoadingSpinner />;
    }

    // Keep the unauthenticated home page hidden while the redirect above lands.
    if (isRoutedToLicenseDefault) {
        return <LoadingSpinner />;
    }

    // The 404 page uses the public layout — same as Product / Integrations — so no sidebar.
    if (isNotFoundPage(pathname, props.path)) {
        return <PublicLayout {...props}>{children}</PublicLayout>;
    }

    // Keep the page hidden while the login redirect above lands.
    if (isRoutedToLogin) {
        return <LoadingSpinner />;
    }

    // Explore Data (stats) is restricted to authenticated @opswat.com users; others get a 404.
    if (isStatsPage) {
        return <PublicLayout {...props}>{canAccessStatsPage(user) ? children : <NotFound />}</PublicLayout>;
    }

    if (isPublic || (isAuthOptional && user?.isLoggedIn !== true)) {
        return <PublicLayout {...props}>{children}</PublicLayout>;
    }

    // 403002: the IP is blocked because of abuse — nothing the admin can reset.
    if (error?.response?.data?.code === 403002) {
        return (
            <PublicLayout {...props}>
                <AccessError link={false} />
            </PublicLayout>
        );
    }

    // 403004: the IP is not within the org's allowlisted ranges — the admin can reset it. MDCI-937: any
    // other 403 on the apikey probe lands here too (the route is not license-gated, so no 403010), so an
    // unrecognised platform code can't fall through to the maintenance page.
    if (error?.response?.data?.code === 403004 || error?.response?.status === 403) {
        return (
            <PublicLayout {...props}>
                <AccessError />
            </PublicLayout>
        );
    }

    if (error) {
        return (
            <PublicLayout {...props}>
                <ServerError />
            </PublicLayout>
        );
    }

    if (!isPublic && !isAuthOptional && !pathname.startsWith('/results')) {
        if (user?.isLoggedIn !== true) {
            return (
                <PublicLayout {...props}>
                    <NotFound />
                </PublicLayout>
            );
        }
    }

    const matchesHideAsNotFound = HIDE_AS_NOT_FOUND_PREFIXES.some((prefix) => pathname.startsWith(prefix));

    if (matchesHideAsNotFound && !canAccessPath(user, pathname)) {
        return (
            <PublicLayout {...props}>
                <NotFound />
            </PublicLayout>
        );
    }

    if (user && !canAccessPath(user, pathname)) {
        return (
            <PublicLayout {...props}>
                <RoleAccessDenied />
            </PublicLayout>
        );
    }

    if (pathname.startsWith('/results')) {
        return <ResultsLayout {...props}>{children}</ResultsLayout>;
    }

    // The blog editor is a full-bleed screen that renders in the public chrome, not the sidebar Layout. It is
    // not under `/blog/`, so the public-route prefix match does not reach it; the guest guard above already
    // 404s everyone who is not logged in.
    if (pathname.startsWith('/blog-editor')) {
        return <PublicLayout {...props}>{children}</PublicLayout>;
    }

    return <Layout {...props}>{children}</Layout>;
};
